/**
 * ⟦C57 · lot 8 · A-174⟧ **Les mots COURTS de l'Atelier et de ses cinq mini-jeux.**
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin […] et
 * aussi moins de texte durant toutes les phases des mini-jeux. »* Pendant le jeu : des icônes, des
 * nombres et des mots isolés (« Parfait ! », « Raté »), jamais une phrase qui oblige à lâcher
 * l'action des yeux. Avant et après (consigne, entre-deux, bilan, résultat) : un titre de 5 mots au
 * plus, une phrase de 12 mots au plus par bloc, et tout le reste derrière « Les dessous ».
 *
 * ## Rien ne disparaît
 *
 * Ce fichier ne porte que la forme COURTE. La forme longue reste là où elle était, déplacée derrière
 * « Les dessous » (`DessousMiniJeu.tsx` pour les mini-jeux, `screens/Atelier/DessousAtelier.tsx`
 * pour l'enveloppe) : les paragraphes d'avant le chantier y sont repris au caractère près
 * (`REGLES_LONGUES` est la règle du pied de l'Atelier d'avant). Aucun barème, aucun timing, aucun
 * pattern n'est touché : seulement le texte et sa place.
 *
 * Pur : aucun DOM, aucun store. `textes.test.ts` compte les mots de chaque texte court avec le même
 * mètre que l'écran (`game/dessous.ts`).
 */

import type { AtelierStage } from "@maxime/shared";

/**
 * La règle de l'étape, en quelques mots, permanente dans le pied de l'Atelier : elle se lit d'un coup
 * d'œil, sans quitter le jeu des yeux. La version entière est `REGLES_LONGUES`, derrière « Les dessous ».
 */
export const REGLES_COURTES: Record<AtelierStage, string> = {
  ecriture: "Chaque phrase dans sa colonne.",
  tournage: "REC quand le curseur est dans le vert.",
  montage: "Frappe quand le marqueur touche la ligne.",
  miniature: "Règle tôt, lâche, laisse l'aiguille se poser.",
  promotion: "Bon moment, bonne plateforme.",
};

/** La règle en UNE phrase, telle que le pied de l'Atelier la portait avant ⟦C57⟧ (SPEC-UI §3.3). */
export const REGLES_LONGUES: Record<AtelierStage, string> = {
  ecriture: "Pousse chaque phrase qui tombe dans la bonne colonne avant qu'elle touche le sol.",
  tournage: "Appuie sur REC quand le curseur est dans le vert : cinq prises, de plus en plus vives.",
  montage: "Frappe sur le temps : chaque cut doit tomber dans la fenêtre du beat.",
  miniature: "Dose le putaclic : trois curseurs, une aiguille vivante, et le chrono qui fige.",
  promotion: "Parie sur les meilleurs moments de ta vidéo et place-les sur la bonne plateforme.",
};

/**
 * Ce que chaque mini-jeu dit sur son écran de consigne : UNE phrase courte par bloc. Les formes
 * longues (la règle entière, les commandes au toucher, ce que fait chaque variante) sont dans les
 * dessous du mini-jeu.
 */
export const CONSIGNE = {
  ecriture: {
    regle: "Pousse chaque phrase dans sa colonne avant le sol.",
    piege: "Bloc rayé : laisse-le filer.",
    doublet: "Deux blocs : déplace le plus bas.",
  },
  tournage: {
    regle: "REC dans le vert, au plus près du centre.",
    pose: "Mode posé : curseur ralenti.",
  },
  montage: {
    regle: "Frappe quand un marqueur touche la ligne.",
    reglePose: "Frappe quand l'anneau est plein.",
  },
  miniature: {
    regle: "Amène l'aiguille dans la zone, puis stabilise-la.",
    mensonge: "⚠ Trop de putaclic : miniature mensongère.",
    promesse: "Annonce d'abord ta promesse.",
  },
  promotion: {
    regle: "Pose tes extraits sur les moments forts, bonne plateforme.",
    metrage: "Taille ton métrage avant de poser.",
  },
} as const;

/**
 * Les commandes, en mots isolés à côté des touches dessinées (← → ↓) : la consigne ne raconte plus
 * le geste, elle le montre. Le geste au toucher et le détail sont dans les dessous.
 */
export const COMMANDES = {
  /** Après `← →` : bouger ; après `↓` : lâcher. */
  ecriture: { deplacer: "bouger", lacher: "lâcher" },
  tournage: "Espace, clic ou toucher",
  montage: "Espace · Entrée · clic",
  miniature: "souris ou ← →",
  promotion: "clic sur la barre",
  reperage: "souris · 1-2-3 puis ← →",
} as const;

/** Le pied de la piste de Montage pendant la manche : les touches, jamais une phrase. */
export function ligneMulticam(pistes: number): string {
  return `F piste A · J piste B${pistes === 3 ? " · Espace piste C" : ""}`;
}

/** Le dérushage, sur l'écran de consigne : combien de marqueurs pour combien de cuts. */
export function ligneReperage(marqueurs: number, cuts: number): string {
  return `${marqueurs} marqueur${marqueurs > 1 ? "s" : ""} pour ${cuts} cuts.`;
}

/** Plus de coupes disponibles : quatre mots. */
export const PLUS_DE_COUPES = "Plus de coupes : laisse finir.";

/** Les mots isolés d'un verdict de Tournage (la bulle de la prise) : « Parfaite ! », « Raté ». */
export const MOTS_VERDICT = {
  doree: "En or !",
  parfaite: "Parfaite !",
  bonne: "Bonne !",
  hors_zone: "Raté",
  eternuement: "Atchoum !",
  sans_appui: "Trop tard",
  trop_tot: "Trop tôt",
} as const;

/** Le verdict du putaclic-o-mètre, en trois mots au plus (le détail est dans les dessous du bilan). */
export const VERDICT_PUTACLIC = {
  mensonger: "⚠ Miniature mensongère.",
  dansLaZone: "Accrocheur sans mentir.",
  trop_sage: "Trop sage.",
  trop_gros: "Trop gros.",
} as const;

/** Les verdicts du putaclic-o-mètre d'avant ⟦C57⟧ (le tiret cadratin y devient une virgule) : ils vivent dans les dessous du bilan. */
export const VERDICT_PUTACLIC_LONG = {
  mensonger: "⚠ Miniature mensongère. Le public va le sentir passer, et un bad buzz sur cette vidéo pèsera double.",
  dansLaZone: "Accrocheur sans mentir. C'est exactement ça.",
  trop_sage: "Trop sage. Personne ne cliquera dessus.",
  trop_gros: "Trop gros, ça sent l'arnaque.",
} as const;

// ───────────────────────── Pendant le jeu : des chiffres et des mots isolés ─────────────────────────

/** L'en-tête du putaclic-o-mètre pendant le réglage : le chrono dit déjà tout, ici un mot. */
export const MOTS_PUTACLIC = {
  fige: "fige à 0",
  figePose: "Entrée : figer",
  valider: "Valider (Entrée)",
  frontiere: "Bande rouge : mensonge.",
} as const;

/** Le chrono et le compteur du repérage (« Marqueur 2/3 ») : des nombres, un mot. */
export function ligneMarqueur(actif: number, total: number): string {
  return `Marqueur ${actif}/${total}`;
}

// ───────────────────────── Entre-deux et bilans : une phrase courte, des nombres ─────────────────────────

/** L'entre-deux du Montage après le dérushage : combien de passages annoncés. */
export function ligneAnnonces(n: number): string {
  return n === 0 ? "Aucun passage annoncé." : `${n} passage${n > 1 ? "s" : ""} annoncé${n > 1 ? "s" : ""}.`;
}

/** Le bilan de l'Écriture : un raté, en une ligne (« → Chute (posé : Histoire) »). */
export function ligneRateEcriture(
  attendue: string | null,
  touche: boolean,
  posee: string,
): string {
  if (attendue === null) return "→ laisser filer";
  return touche ? `→ ${attendue} (posé : ${posee})` : `→ ${attendue} (tombé)`;
}

/** Le bilan du Tournage : « 3/5 dans le vert · 1 en or ». */
export function ligneBilanTournage(bonnes: number, total: number, ors: number): string {
  return `${bonnes}/${total} dans le vert${ors > 0 ? ` · ${ors} en or` : ""}`;
}

/** Le bilan de La Découpe, en mode compte : la heatmap reste chez le serveur. */
export function ligneDecoupeServeur(extraits: number): string {
  return `${extraits} extrait${extraits > 1 ? "s" : ""} posé${extraits > 1 ? "s" : ""}, noté${extraits > 1 ? "s" : ""} par le serveur.`;
}

/** Le métrage de La Découpe, en jeu : un budget et un nombre d'extraits. */
export function ligneMetrage(budget: string, extraits: number): string {
  return `Répartis ${budget} entre ${extraits} extraits.`;
}

/** Après le métrage : ce qu'on a engagé. */
export function ligneMetrageEngage(depense: string, budget: string): string {
  return `${depense} engagés sur ${budget}.`;
}
